import { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatMoney, formatDate } from "@/lib/constants";
import { Plus, Trash2, Edit, Package } from "lucide-react";
import { useQuickAdd } from "@/lib/useQuickAdd";
import { WorkLogMaterialsDialog } from "@/components/WorkLogMaterialsDialog";

const CURRENT_YEAR = new Date().getFullYear();

const BUCKETS = [
  { key: "previous", label: `Önceki Yıl (${CURRENT_YEAR - 1})`, year: CURRENT_YEAR - 1 },
  { key: "current", label: `Cari Yıl (${CURRENT_YEAR})`, year: CURRENT_YEAR },
  { key: "next", label: `Sonraki Yıl (${CURRENT_YEAR + 1})`, year: CURRENT_YEAR + 1 },
];

const STATUS_OPTS = {
  previous: [
    { v: "baslangic", l: "Başlangıç Bilgisi" },
    { v: "sonuc", l: "Sonuç Bilgisi" },
  ],
  current: [
    { v: "devam", l: "Devam Ediyor" },
    { v: "tamamlandi", l: "Tamamlandı" },
  ],
  next: [{ v: "planlandi", l: "Planlandı" }],
};

function emptyLog(bucket) {
  const b = BUCKETS.find((x) => x.key === bucket);
  return {
    village_id: "",
    category_id: "",
    quantity: 0,
    unit: "adet",
    amount: 0,
    date: new Date().toISOString().slice(0, 10),
    year: b.year,
    time_bucket: bucket,
    description: "",
    status: STATUS_OPTS[bucket][0].v,
  };
}

export default function WorkLogsPage() {
  const [bucket, setBucket] = useState("current");
  const [logs, setLogs] = useState([]);
  const [villages, setVillages] = useState([]);
  const [cats, setCats] = useState([]);
  const [villageFilter, setVillageFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLog("current"));
  const [materialsLog, setMaterialsLog] = useState(null);

  const villageMap = useMemo(() => Object.fromEntries(villages.map((v) => [v.id, v.name])), [villages]);
  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  const load = () => {
    const b = BUCKETS.find((x) => x.key === bucket);
    api.get("/work-logs", { params: { time_bucket: bucket, year: b.year } }).then((r) => setLogs(r.data));
  };

  useEffect(() => {
    api.get("/villages").then((r) => setVillages(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  useEffect(() => { load(); }, [bucket]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyLog(bucket));
    setOpen(true);
  };
  const quickDone = useQuickAdd((vid) => { setEditing(null); setForm({ ...emptyLog("current"), village_id: vid }); setOpen(true); }, open);

  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...l });
    setOpen(true);
  };

  const handleCategoryChange = (cid) => {
    const c = catMap[cid];
    setForm({ ...form, category_id: cid, unit: c?.default_unit || form.unit });
  };

  const save = async () => {
    if (!form.village_id || !form.category_id) {
      toast.error("Köy ve kategori zorunludur");
      return;
    }
    try {
      if (editing) {
        await api.put(`/work-logs/${editing.id}`, form);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post("/work-logs", form);
        toast.success("Kayıt oluşturuldu");
      }
      setOpen(false);
      load();
      if (!editing) quickDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const remove = async (l) => {
    if (!window.confirm("Kayıt silinsin mi?")) return;
    await api.delete(`/work-logs/${l.id}`);
    toast.success("Silindi");
    load();
  };

  const filtered = logs.filter((l) => villageFilter === "all" || l.village_id === villageFilter);
  const totalAmount = filtered.reduce((s, l) => s + (l.amount || 0), 0);

  return (
    <AppLayout
      title="Çalışma Kayıtları"
      subtitle="Köy bazlı iş kayıtları — önceki yıl, cari yıl ve sonraki yıl planlama"
      actions={
        <Button onClick={openNew} data-testid="new-work-log-button" className="bg-emerald-700 hover:bg-emerald-800">
          <Plus className="h-4 w-4 mr-1.5" /> Yeni Kayıt
        </Button>
      }
    >
      <Tabs value={bucket} onValueChange={setBucket} className="mb-6 max-w-full overflow-x-auto">
        <TabsList data-testid="work-log-year-tabs">
          {BUCKETS.map((b) => (
            <TabsTrigger key={b.key} value={b.key} data-testid={`work-log-year-tab-${b.key}`}>
              {b.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between mb-4 gap-4">
        <Select value={villageFilter} onValueChange={setVillageFilter}>
          <SelectTrigger className="w-72" data-testid="village-filter-select">
            <SelectValue placeholder="Köy seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Köyler</SelectItem>
            {villages.map((v) => (
              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-600">
          Toplam: <span className="font-mono font-semibold text-slate-900">{formatMoney(totalAmount)}</span>
          <span className="mx-2 text-slate-300">•</span>
          <span className="text-slate-700">{filtered.length} kayıt</span>
        </div>
      </div>

      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="work-logs-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Tarih</TableHead>
              <TableHead>Köy</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead className="text-right">Miktar</TableHead>
              <TableHead className="text-right">Tutar</TableHead>
              <TableHead className="text-right">Malzeme</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead>Açıklama</TableHead>
              <TableHead className="w-24">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="row-hover">
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10 text-slate-500">
                  Bu zaman diliminde kayıt bulunmuyor.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`work-log-row-${l.id}`}>
                <TableCell className="font-mono text-xs whitespace-nowrap">{formatDate(l.date)}</TableCell>
                <TableCell className="font-medium">{villageMap[l.village_id] || "-"}</TableCell>
                <TableCell>{catMap[l.category_id]?.name || "-"}</TableCell>
                <TableCell className="text-right font-mono">{l.quantity} {l.unit}</TableCell>
                <TableCell className="text-right font-mono">{formatMoney(l.amount)}</TableCell>
                <TableCell className="text-right font-mono whitespace-nowrap" data-testid={`work-log-material-total-${l.id}`}>
                  {l.material_count ? formatMoney(l.material_total) : <span className="text-slate-400">-</span>}
                </TableCell>
                <TableCell><Badge variant="secondary">{Object.values(STATUS_OPTS).flat().find((o) => o.v === l.status)?.l || l.status}</Badge></TableCell>
                <TableCell className="max-w-[280px] truncate text-slate-600 text-sm">{l.description}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setMaterialsLog(l)} title="Malzemeler"
                            data-testid={`work-log-materials-${l.id}`}>
                      <Package className="h-4 w-4 text-emerald-700" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(l)} data-testid={`edit-work-log-${l.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(l)} data-testid={`delete-work-log-${l.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {materialsLog && (
        <WorkLogMaterialsDialog log={materialsLog}
          title={`${formatDate(materialsLog.date)} — ${villageMap[materialsLog.village_id] || ""} — ${catMap[materialsLog.category_id]?.name || ""}`}
          onClose={() => { setMaterialsLog(null); load(); }} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="work-log-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Çalışma Kaydını Düzenle" : "Yeni Çalışma Kaydı"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Köy *</Label>
              <Select value={form.village_id} onValueChange={(v) => setForm({ ...form, village_id: v })}>
                <SelectTrigger data-testid="work-log-village-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {villages.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kategori *</Label>
              <Select value={form.category_id} onValueChange={handleCategoryChange}>
                <SelectTrigger data-testid="work-log-category-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Miktar</Label>
              <Input type="number" step="0.01" value={form.quantity}
                     onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 0 })}
                     data-testid="work-log-quantity-input" />
            </div>
            <div>
              <Label>Birim</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                     data-testid="work-log-unit-input" />
            </div>
            <div>
              <Label>Tutar (TL)</Label>
              <Input type="number" step="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: Number(e.target.value) || 0 })}
                     data-testid="work-log-amount-input" />
            </div>
            <div>
              <Label>Tarih</Label>
              <DateField value={form.date} className="w-full" clearable={false} testId="work-log-date-input"
                     onChange={(v) => setForm({ ...form, date: v })} />
            </div>
            <div className="col-span-2">
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTS[form.time_bucket].map((o) => (
                    <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Açıklama / Not (isteğe bağlı)</Label>
              <Textarea value={form.description} maxLength={1000}
                        placeholder="Ör. yapılan işin ayrıntıları, sonuç notu..."
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="work-log-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="work-log-submit-button">
              {editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
